import { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, MenuItem, Pagination, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getApiError } from "../api/errors";
import { getPurchaseOrders } from "../api/purchaseOrders";
import PurchaseOrderStatusChip from "../components/PurchaseOrderStatusChip";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";

const PAGE_SIZE = 20;
const STATUSES = ["DRAFT", "ORDERED", "PARTIALLY_RECEIVED", "RECEIVED", "CANCELLED"];
const sum = (items, field) => items.reduce((total, item) => total + Number(item[field] || 0), 0);

function knownCost(items) {
  const known = items.filter((item) => item.expected_unit_cost !== null && item.expected_unit_cost !== undefined);
  return {
    value: known.reduce((total, item) => total + Number(item.expected_unit_cost) * Number(item.ordered_quantity), 0),
    unknown: items.length - known.length,
  };
}

export default function PurchaseOrders() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { selectedStore, selectedWorkspace } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const amount = (value) => Number(value).toLocaleString(i18n.language, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  const date = (value) => value ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(value)) : "—";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getPurchaseOrders({ page, page_size: PAGE_SIZE, ...(status ? { status } : {}) });
      setItems(response.data?.items || []);
      setTotal(Number(response.data?.total || 0));
    } catch (requestError) {
      setError(getApiError(requestError, t("purchaseOrders.loadError")));
    } finally {
      setLoading(false);
    }
  }, [page, status, t]);

  useEffect(() => { load(); }, [load, selectedStore?.public_id, selectedWorkspace?.public_id]);

  const summary = (order) => {
    const cost = knownCost(order.items || []);
    return {
      cost: cost.unknown === (order.items || []).length
        ? t("purchaseOrders.unknown")
        : `${amount(cost.value)}${cost.unknown ? ` · ${t("purchaseOrders.partialCost")}` : ""}`,
      received: sum(order.items || [], "received_quantity"),
      outstanding: sum(order.items || [], "outstanding_quantity"),
    };
  };

  return (
    <MainLayout>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={900}>{t("purchaseOrders.title")}</Typography>
          <Typography color="text.secondary">{t("purchaseOrders.subtitle")}</Typography>
        </Box>
        <TextField
          select size="small" label={t("purchaseOrders.filterStatus")} value={status}
          onChange={(event) => { setStatus(event.target.value); setPage(1); }} sx={{ minWidth: 190 }}
        >
          <MenuItem value="">{t("common.all")}</MenuItem>
          {STATUSES.map((value) => <MenuItem key={value} value={value}>{t(`purchaseOrders.status.${value}`)}</MenuItem>)}
        </TextField>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Typography color="text.secondary">{t("common.loading")}</Typography>}
      {!loading && !items.length && <Alert severity="info">{t("purchaseOrders.empty")}</Alert>}

      <TableContainer component={Card} sx={{ display: { xs: "none", md: "block" } }}>
        <Table aria-label={t("purchaseOrders.tableLabel")}>
          <TableHead><TableRow>
            <TableCell>{t("purchaseOrders.number")}</TableCell><TableCell>{t("common.status")}</TableCell>
            <TableCell>{t("purchaseOrders.supplier")}</TableCell><TableCell>{t("purchaseOrders.created")}</TableCell>
            <TableCell>{t("purchaseOrders.ordered")}</TableCell><TableCell align="right">{t("purchaseOrders.items")}</TableCell>
            <TableCell align="right">{t("purchaseOrders.knownCost")}</TableCell><TableCell align="right">{t("purchaseOrders.receivedOutstanding")}</TableCell>
            <TableCell align="right">{t("common.actions")}</TableCell>
          </TableRow></TableHead>
          <TableBody>{items.map((order) => {
            const values = summary(order);
            return <TableRow key={order.public_id} hover>
              <TableCell><Typography fontWeight={850}>{order.order_number}</Typography></TableCell>
              <TableCell><PurchaseOrderStatusChip status={order.status} /></TableCell>
              <TableCell>{order.supplier_name || "—"}</TableCell><TableCell>{date(order.created_at)}</TableCell>
              <TableCell>{date(order.ordered_at)}</TableCell><TableCell align="right">{order.items?.length || 0}</TableCell>
              <TableCell align="right">{values.cost}</TableCell>
              <TableCell align="right">{values.received} / {values.outstanding}</TableCell>
              <TableCell align="right"><Button onClick={() => navigate(`/purchase-orders/${order.public_id}`)}>{t("purchaseOrders.open")}</Button></TableCell>
            </TableRow>;
          })}</TableBody>
        </Table>
      </TableContainer>

      <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" } }}>
        {items.map((order) => {
          const values = summary(order);
          return <Card key={order.public_id}><CardContent>
            <Stack direction="row" justifyContent="space-between" gap={1} alignItems="flex-start">
              <Typography fontWeight={900}>{order.order_number}</Typography><PurchaseOrderStatusChip status={order.status} />
            </Stack>
            <Typography color="text.secondary" mt={1}>{order.supplier_name || t("purchaseOrders.noSupplier")}</Typography>
            <Typography variant="body2" mt={1}>{t("purchaseOrders.mobileSummary", { items: order.items?.length || 0, received: values.received, outstanding: values.outstanding })}</Typography>
            <Typography variant="body2">{t("purchaseOrders.knownCost")}: {values.cost}</Typography>
            <Button fullWidth variant="outlined" sx={{ mt: 1.5 }} onClick={() => navigate(`/purchase-orders/${order.public_id}`)}>{t("purchaseOrders.open")}</Button>
          </CardContent></Card>;
        })}
      </Stack>
      {total > PAGE_SIZE && <Pagination sx={{ mt: 2 }} page={page} count={Math.ceil(total / PAGE_SIZE)} onChange={(_, value) => setPage(value)} />}
    </MainLayout>
  );
}
